import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { usePermissions } from '@shared/hooks/usePermissions';
import { CourierFileHeader, CourierFilePaymentsAgGridTable, ErrorState, LoadingState } from '../';
import { useAgGridColumns, useCourierFileDetail, useCourierFilePayments } from '../../hooks';
import { AddPaymentButton, DeductPaymentButton, ExportPaymentsButton } from '../interaction';

export const Main = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('codCourierFileDetail');
  const { canAccess } = usePermissions();
  const fileId = id ? parseInt(id, 10) : 0;

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const {
    courierFile,
    isLoading: isLoadingFile,
    isFetching: isFetchingFile,
    error: errorFile,
    refetch: refetchFile,
  } = useCourierFileDetail({
    fileId,
    enabled: !!fileId,
  });

  const {
    payments,
    meta,
    isLoading: isLoadingPayments,
    isFetching: isFetchingPayments,
    error: errorPayments,
    handleSortingChange,
    sorting,
    refetch: refetchPayments,
    changePage,
  } = useCourierFilePayments({
    fileId,
    enabled: !!fileId,
  });

  const { columnDefs } = useAgGridColumns();

  // 🎯 Efecto para marcar que la carga inicial terminó
  useEffect(() => {
    if (!isLoadingFile && !isLoadingPayments && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoadingFile, isLoadingPayments, isInitialLoad]);

  // Configurar breadcrumbs para la topbar
  const breadcrumbs = useMemo(
    () => [
      { label: t('breadcrumb.courier_files', 'Archivos Courier'), path: '/cod-payments/courier-files' },
      { label: `${t('breadcrumb.detail', 'Archivo')} #${fileId}` },
    ],
    [t, fileId],
  );

  // Estados para controlar los modales
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);
  const [isDeductPaymentModalOpen, setIsDeductPaymentModalOpen] = useState(false);

  // Función helper para normalizar el estado
  const normalizeStatus = (status: string | undefined): string => {
    if (!status) return '';
    return status.toUpperCase();
  };

  // Verificar si el archivo está en estado imported (solo entonces se pueden añadir/descontar pagos)
  const isImportedStatus = useMemo(() => {
    if (!courierFile) return false;
    const normalizedStatus = normalizeStatus(courierFile.status);
    return normalizedStatus === 'IMPORTED';
  }, [courierFile]);

  // 🎯 Verificar permiso para añadir pagos
  const canAddPayment = canAccess(['tenant_3pl'], ['cod-payments.payments.add']);

  // Configurar acciones del header (solo mostrar si el archivo está en estado imported)
  const headerActions = useMemo(() => {
    if (!isImportedStatus || !canAddPayment) return [];

    return [
      {
        id: 'add-payment',
        label: t('addPayment.button', 'Añadir Pago'),
        onClick: () => setIsAddPaymentModalOpen(true),
      },
      {
        id: 'deduct-payment',
        label: t('deductPayment.button', 'Descontar Pago'),
        onClick: () => setIsDeductPaymentModalOpen(true),
      },
    ];
  }, [t, isImportedStatus, canAddPayment]);

  // Establecer breadcrumbs y acciones en la topbar
  usePageNavigation({
    breadcrumbs,
    headerActions,
  });

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return (
      <div className="px-6 pt-4">
        <LoadingState message={t('loading.payments', 'Cargando pagos...')} />
      </div>
    );
  }

  // Estado de error del archivo
  if (errorFile) {
    return (
      <div className="px-6 pt-4">
        <ErrorState message={errorFile} onRetry={refetchFile} />
      </div>
    );
  }

  // Si no hay archivo después de la carga inicial, mostrar error
  if (!courierFile) {
    return (
      <div className="px-6 pt-4">
        <ErrorState message={t('error.file_not_found', 'No se encontró el archivo')} onRetry={refetchFile} />
      </div>
    );
  }

  // Total de pagos para detectar estado vacío
  const total = meta?.total ?? 0;

  return (
    <div className="px-6 pt-4">
      {/* Header con información del archivo */}
      <CourierFileHeader courierFile={courierFile} isFetching={isFetchingFile} />

      {/* Botón de exportar */}
      {total > 0 && (
        <div className="flex justify-end mb-4">
          <ExportPaymentsButton
            fileId={fileId}
            sorting={sorting ? { column: sorting.field, direction: sorting.direction } : undefined}
          />
        </div>
      )}

      {/* Tabla de pagos con AG-Grid */}
      {errorPayments ? (
        <ErrorState message={errorPayments} onRetry={refetchPayments} className="h-40" />
      ) : (
        <>
          {/* 🎯 Estado vacío: siempre en el DOM, controlado por opacity y height */}
          <div
            className={`transition-opacity duration-200 overflow-hidden ${
              total === 0 ? 'opacity-100 h-auto' : 'opacity-0 h-0 pointer-events-none'
            }`}
          >
            <EmptyView
              type="emptyState"
              icon={EMPTY_VIEW_ICONS.DEFAULT}
              title={t('empty.title', 'No hay pagos')}
              description={t('empty.description', 'No hay pagos en este archivo')}
            />
          </div>

          {/* 🎯 Vista de pagos: siempre en el DOM, controlado por opacity y height */}
          <div
            className={`mb-4 transition-opacity duration-200 overflow-hidden ${
              total > 0 ? 'opacity-100 h-auto' : 'opacity-0 h-0'
            }`}
          >
            <CourierFilePaymentsAgGridTable
              payments={payments}
              columnDefs={columnDefs}
              isLoading={isLoadingPayments}
              isFetching={isFetchingPayments}
              sorting={sorting}
              onSortingChange={handleSortingChange}
            />
          </div>

          {/* Paginación */}
          {meta && meta.last_page > 1 && total > 0 && (
            <div className="flex justify-between items-center mt-4 px-2">
              <div className="text-sm text-muted-foreground">
                {t('pagination.page', 'Página')} {meta.current_page} {t('pagination.of', 'de')} {meta.last_page}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => changePage(meta.current_page - 1)}
                  disabled={meta.current_page === 1 || isFetchingPayments}
                  className="px-3 py-1 text-sm border border-border rounded-md hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('pagination.previous', 'Anterior')}
                </button>
                <button
                  onClick={() => changePage(meta.current_page + 1)}
                  disabled={meta.current_page === meta.last_page || isFetchingPayments}
                  className="px-3 py-1 text-sm border border-border rounded-md hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('pagination.next', 'Siguiente')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modales */}
      <AddPaymentButton
        isOpen={isAddPaymentModalOpen}
        onClose={() => setIsAddPaymentModalOpen(false)}
        fileId={fileId}
        fileStatus={courierFile.status}
      />
      <DeductPaymentButton
        isOpen={isDeductPaymentModalOpen}
        onClose={() => setIsDeductPaymentModalOpen(false)}
        fileId={fileId}
        fileStatus={courierFile.status}
      />
    </div>
  );
};
